import React, { useEffect, useMemo, useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';
import { ArrowDownToLine, RotateCcw, X, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { DateField } from '../shared/DateField';

/**
 * Distributions panel for the Alternatives tab. Lists all PE distributions
 * across the selected portfolio with a Recall action per row.
 *
 * Recall workflow (Phase 3):
 *   POST /api/alternatives/phase3/distributions/{id}/recall
 *   body: { amount, recallDate, reasonCode, reason }
 *
 * Reversals are submitted with reasonCode=REVERSAL and route through the same
 * endpoint — the server stores them as a negative recall row (append-only).
 */

interface Distribution {
  id: number;
  commitmentId: number;
  fundId?: number | null;
  portfolioId: number;
  distributionDate: string;
  paymentDate?: string | null;
  grossDistribution: number;
  netDistribution?: number;
  recallableAmount?: number;
  returnOfCapital?: number;
  preferredReturn?: number;
  capitalGains?: number;
  status: string;
  currency?: string;
  fundName?: string | null;
  distributionType?: string | null;
}

interface RecallRow {
  id: number;
  commitmentId: number;
  recallAmount: number;
  recallDate: string;
  reasonCode: string;
  reason: string | null;
  postedTradeId: number | null;
  createdAt: string;
}

interface Commitment {
  id: number;
  fundId?: number | null;
  portfolioId: number;
  commitmentCurrency?: string;
  sideLetterNotes?: string;
}

const fmtMoney = (v: number | null | undefined, ccy = 'USD') => {
  if (v == null || isNaN(v)) return '—';
  const sign = ccy === 'USD' ? '$' : `${ccy} `;
  const a = Math.abs(v);
  if (a >= 1e9) return `${v < 0 ? '-' : ''}${sign}${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${v < 0 ? '-' : ''}${sign}${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${v < 0 ? '-' : ''}${sign}${(a / 1e3).toFixed(0)}K`;
  return `${v < 0 ? '-' : ''}${sign}${a.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const parseFundName = (notes?: string): string | null => {
  if (!notes) return null;
  const match = notes.match(/Fund:\s*([^|]+)/);
  return match ? match[1].trim() : null;
};

const PEDistributionsPanel: React.FC = () => {
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<number | null>(null);
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [distributions, setDistributions] = useState<Distribution[]>([]);
  const [loading, setLoading] = useState(false);
  const [recallModal, setRecallModal] = useState<Distribution | null>(null);
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null);

  const flash = (tone: 'success' | 'error', msg: string) => {
    setToast({ tone, msg });
    window.setTimeout(() => setToast(null), 4500);
  };

  // Load portfolios
  useEffect(() => {
    axios
      .get('/api/dimensions/portfolios')
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.content || [];
        setPortfolios(list);
        if (list.length > 0) setSelectedPortfolio(list[0].id);
      })
      .catch(() => setPortfolios([]));
  }, []);

  // Load commitments + per-commitment distributions for selected portfolio
  const loadDistributions = (portfolioId: number) => {
    setLoading(true);
    axios
      .get(`/api/alternatives/commitments/portfolio/${portfolioId}`)
      .then(async (r) => {
        const cs: Commitment[] =
          Array.isArray(r.data) ? r.data : Array.isArray(r.data?.content) ? r.data.content : [];
        setCommitments(cs);
        if (cs.length === 0) {
          setDistributions([]);
          return;
        }
        // One batch round trip for all commitments (was a per-commitment fan-out).
        const flat: Distribution[] = await axios
          .get(`/api/alternatives/distributions/by-commitments`, {
            params: { ids: cs.map((c) => c.id).join(',') },
          })
          .then((res) => (Array.isArray(res.data) ? res.data : []))
          .catch(() => []);
        // Sort newest first.
        flat.sort((a, b) => (b.distributionDate || '').localeCompare(a.distributionDate || ''));
        setDistributions(flat);
      })
      .catch(() => {
        setCommitments([]);
        setDistributions([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (selectedPortfolio != null) loadDistributions(selectedPortfolio);
  }, [selectedPortfolio]);

  const commitmentById = useMemo(() => {
    const m = new Map<number, Commitment>();
    commitments.forEach((c) => m.set(c.id, c));
    return m;
  }, [commitments]);

  const totals = useMemo(() => {
    return distributions.reduce(
      (acc, d) => {
        acc.gross += d.grossDistribution || 0;
        acc.recallable += d.recallableAmount || 0;
        acc.count += 1;
        return acc;
      },
      { gross: 0, recallable: 0, count: 0 }
    );
  }, [distributions]);

  return (
    <div className="px-6 py-6 text-slate-100">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <ArrowDownToLine className="w-5 h-5 text-emerald-400" />
            PE Distributions
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            All distributions received across the selected portfolio. Recall a recallable
            distribution to reverse the cash and decrement{' '}
            <code className="text-violet-300">distributedAmount</code>.
          </p>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
            Portfolio
          </label>
          <select
            value={selectedPortfolio ?? ''}
            onChange={(e) => setSelectedPortfolio(Number(e.target.value))}
            className="bg-slate-900 border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-violet-500/50 min-w-[220px]"
          >
            {portfolios.map((p) => (
              <option key={p.id} value={p.id}>
                {p.portfolioName || p.name || `Portfolio #${p.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Kpi label="Distributions" value={totals.count.toString()} />
        <Kpi label="Gross received" value={fmtMoney(totals.gross)} />
        <Kpi label="Recallable balance" value={fmtMoney(totals.recallable)} tone="warning" />
      </div>

      <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-5 py-12 text-center text-slate-500 text-sm">Loading…</div>
        ) : distributions.length === 0 ? (
          <div className="px-5 py-12 text-center text-slate-500 text-sm">
            No distributions on this portfolio yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-950/40">
                <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="text-left px-5 py-2 font-semibold">Date</th>
                  <th className="text-left px-3 py-2 font-semibold">Fund / Commitment</th>
                  <th className="text-left px-3 py-2 font-semibold">Type</th>
                  <th className="text-right px-3 py-2 font-semibold">Gross</th>
                  <th className="text-right px-3 py-2 font-semibold">RoC</th>
                  <th className="text-right px-3 py-2 font-semibold">Pref</th>
                  <th className="text-right px-3 py-2 font-semibold">Cap Gains</th>
                  <th className="text-right px-3 py-2 font-semibold">Recallable</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="text-right px-5 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {distributions.map((d) => {
                  const c = commitmentById.get(d.commitmentId);
                  const fundName =
                    d.fundName || parseFundName(c?.sideLetterNotes) || `Commitment #${d.commitmentId}`;
                  const ccy = d.currency || c?.commitmentCurrency || 'USD';
                  const recallable = d.recallableAmount || 0;
                  const statusColor =
                    d.status === 'RECONCILED' || d.status === 'RECEIVED'
                      ? 'bg-emerald-500/15 text-emerald-300'
                      : d.status === 'APPROVED' || d.status === 'REVIEWED'
                      ? 'bg-blue-500/15 text-blue-300'
                      : 'bg-slate-700/30 text-slate-400';
                  const canRecall =
                    recallable > 0 && (d.status === 'RECEIVED' || d.status === 'RECONCILED');
                  return (
                    <tr key={d.id} className="border-t border-white/5 hover:bg-white/5">
                      <td className="px-5 py-2.5 text-slate-200 whitespace-nowrap">
                        {d.distributionDate}
                      </td>
                      <td className="px-3 py-2.5 text-slate-300">
                        <div className="font-medium text-slate-200">{fundName}</div>
                        <div className="text-[10px] text-slate-500">
                          Distribution #{d.id} • Commitment #{d.commitmentId}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-400 text-[11px]">
                        {d.distributionType || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-200 font-mono">
                        {fmtMoney(d.grossDistribution, ccy)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-400 font-mono">
                        {fmtMoney(d.returnOfCapital, ccy)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-400 font-mono">
                        {fmtMoney(d.preferredReturn, ccy)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-400 font-mono">
                        {fmtMoney(d.capitalGains, ccy)}
                      </td>
                      <td
                        className={`px-3 py-2.5 text-right font-mono ${
                          recallable > 0 ? 'text-amber-300 font-semibold' : 'text-slate-500'
                        }`}
                      >
                        {fmtMoney(recallable, ccy)}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${statusColor}`}
                        >
                          {d.status}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-right">
                        {canRecall ? (
                          <button
                            onClick={() => setRecallModal(d)}
                            className="inline-flex items-center gap-1 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 px-2.5 py-1 rounded text-[11px] font-semibold"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Recall
                          </button>
                        ) : recallable > 0 ? (
                          <span className="text-[11px] text-slate-600">Awaiting receipt</span>
                        ) : (
                          <span className="text-[11px] text-slate-600">Not recallable</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && (
        <div
          className={`fixed top-20 right-6 z-50 rounded-lg px-4 py-3 text-sm shadow-2xl border flex items-center gap-2 ${
            toast.tone === 'success'
              ? 'bg-emerald-900/90 border-emerald-500/40 text-emerald-100'
              : 'bg-rose-900/90 border-rose-500/40 text-rose-100'
          }`}
        >
          {toast.tone === 'success' ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
          {toast.msg}
        </div>
      )}

      {recallModal && (
        <RecallModal
          distribution={recallModal}
          commitment={commitmentById.get(recallModal.commitmentId)}
          onClose={() => setRecallModal(null)}
          onDone={(msg) => {
            flash('success', msg);
            setRecallModal(null);
            if (selectedPortfolio != null) loadDistributions(selectedPortfolio);
          }}
          onError={(msg) => flash('error', msg)}
        />
      )}
    </div>
  );
};

const Kpi: React.FC<{
  label: string;
  value: string;
  tone?: 'neutral' | 'positive' | 'warning';
}> = ({ label, value, tone = 'neutral' }) => {
  const color =
    tone === 'positive' ? 'text-emerald-300' : tone === 'warning' ? 'text-amber-300' : 'text-white';
  return (
    <div className="bg-slate-900/40 border border-white/5 rounded-lg px-4 py-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        {label}
      </div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
};

const RecallModal: React.FC<{
  distribution: Distribution;
  commitment?: Commitment;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}> = ({ distribution, commitment, onClose, onDone, onError }) => {
  const ccy = distribution.currency || commitment?.commitmentCurrency || 'USD';
  const today = getTodayDate();
  const [amount, setAmount] = useState<string>('');
  const [recallDate, setRecallDate] = useState<string>(today);
  const [reasonCode, setReasonCode] = useState<string>('FOLLOW_ON');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [recalls, setRecalls] = useState<RecallRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    setLoadingHistory(true);
    axios
      .get(`/api/alternatives/phase3/distributions/${distribution.id}/recalls`)
      .then((r) => setRecalls(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRecalls([]))
      .finally(() => setLoadingHistory(false));
  }, [distribution.id]);

  const netRecalled = recalls.reduce((s, r) => s + (r.recallAmount || 0), 0);
  const remainingRecallable = (distribution.recallableAmount || 0) - netRecalled;

  const fundName =
    distribution.fundName || parseFundName(commitment?.sideLetterNotes) || `Commitment #${distribution.commitmentId}`;

  const handleSubmit = async () => {
    const n = parseFloat(amount.replace(/[$,]/g, ''));
    if (isNaN(n) || n <= 0) {
      onError('Recall amount must be a positive number');
      return;
    }
    if (reasonCode !== 'REVERSAL' && n > remainingRecallable) {
      onError(`Amount exceeds remaining recallable balance ${fmtMoney(remainingRecallable, ccy)}`);
      return;
    }
    setSubmitting(true);
    try {
      const resp = await axios.post(
        `/api/alternatives/phase3/distributions/${distribution.id}/recall`,
        {
          amount: n,
          recallDate,
          reasonCode,
          reason: reason.trim() || null,
        }
      );
      const postedTradeId = resp.data?.postedTradeId;
      onDone(
        `Recall booked — ${fmtMoney(n, ccy)} (${reasonCode}) recorded${
          postedTradeId ? `, trade #${postedTradeId}` : ''
        }.`
      );
    } catch (err: any) {
      onError(err?.response?.data?.message || err?.message || 'Recall failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-amber-400" />
              Recall distribution
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {fundName} • Distribution #{distribution.id} • {distribution.distributionDate}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white p-1 rounded hover:bg-white/5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Source distribution snapshot */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Gross" value={fmtMoney(distribution.grossDistribution, ccy)} />
            <Field
              label="Original recallable"
              value={fmtMoney(distribution.recallableAmount, ccy)}
            />
            <Field
              label="Already recalled (net)"
              value={fmtMoney(netRecalled, ccy)}
              tone={netRecalled > 0 ? 'warning' : 'neutral'}
            />
            <Field
              label="Remaining recallable"
              value={fmtMoney(remainingRecallable, ccy)}
              tone="positive"
            />
          </div>

          {/* History */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5">
              Recall ledger
            </div>
            {loadingHistory ? (
              <div className="text-xs text-slate-500 italic">Loading history…</div>
            ) : recalls.length === 0 ? (
              <div className="text-xs text-slate-500 italic">No prior recalls.</div>
            ) : (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-slate-500 uppercase tracking-wider text-[9px] border-b border-white/5">
                    <th className="text-left py-1 font-semibold">Date</th>
                    <th className="text-left py-1 font-semibold">Reason</th>
                    <th className="text-right py-1 font-semibold">Amount</th>
                    <th className="text-left py-1 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {recalls.map((r) => (
                    <tr key={r.id} className="border-b border-white/5">
                      <td className="py-1.5 text-slate-400">{r.recallDate}</td>
                      <td className="py-1.5">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                            r.reasonCode === 'REVERSAL'
                              ? 'bg-rose-500/15 text-rose-300'
                              : 'bg-amber-500/15 text-amber-300'
                          }`}
                        >
                          {r.reasonCode}
                        </span>
                      </td>
                      <td
                        className={`py-1.5 text-right font-mono ${
                          r.recallAmount < 0 ? 'text-rose-300' : 'text-amber-300'
                        }`}
                      >
                        {fmtMoney(r.recallAmount, ccy)}
                      </td>
                      <td className="py-1.5 text-slate-500 truncate max-w-[200px]">
                        {r.reason || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Form */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/5">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                Amount ({ccy})
              </label>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={fmtMoney(remainingRecallable, ccy).replace(/[$,]/g, '')}
                className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500/50"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                Recall date
              </label>
              <DateField
                value={recallDate ?? ''}
                className="w-full"
                onChange={iso => setRecallDate(iso)}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                Reason code
              </label>
              <select
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500/50"
              >
                <option value="FOLLOW_ON">FOLLOW_ON — recall for new investment</option>
                <option value="EXPENSE">EXPENSE — fund-level expense recall</option>
                <option value="OTHER">OTHER — sponsor-discretionary</option>
                <option value="REVERSAL">REVERSAL — undo a prior recall</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1 block">
                Notes (optional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Free text — captured on the audit row"
                className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500/50"
              />
            </div>
          </div>

          {reasonCode === 'REVERSAL' && (
            <div className="bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded-lg px-3 py-2 text-[11px] flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Reversals append a negative-amount recall row. The source distribution is left
                untouched (append-only contract). Use the original recall amount to fully unwind.
              </span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !amount}
            className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {submitting ? 'Submitting…' : 'Confirm recall'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  tone?: 'neutral' | 'positive' | 'warning';
}> = ({ label, value, tone = 'neutral' }) => {
  const color =
    tone === 'positive' ? 'text-emerald-300' : tone === 'warning' ? 'text-amber-300' : 'text-slate-200';
  return (
    <div className="bg-slate-950/40 border border-white/5 rounded-lg px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">
        {label}
      </div>
      <div className={`text-sm font-semibold ${color} font-mono`}>{value}</div>
    </div>
  );
};

export default PEDistributionsPanel;
